import React, { useState, useMemo } from 'react';
import { BookOpen, Search, FileText, Rocket, HelpCircle, Code, ThumbsUp, ThumbsDown, ChevronRight, Home } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface DocumentationArticle {
  id: string;
  title: string;
  category: string;
  description: string;
  content: string;
  tags: string[];
  lastUpdated: string;
}

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface DocumentationPageProps {
  onNavigate?: (path: string) => void;
}

const documentationData: DocumentationArticle[] = [
  {
    id: 'getting-started',
    title: 'Getting Started with TimeTrack Pro',
    category: 'Getting Started',
    description: 'Learn the basics of TimeTrack Pro and how to get up and running quickly.',
    content: `
# Getting Started with TimeTrack Pro

Welcome to TimeTrack Pro! This guide will help you get started with the application.

## Overview
TimeTrack Pro is a comprehensive time tracking and management solution designed for professional services organizations. It helps you track time, manage approvals, and generate insightful reports.

## Key Features
- **Time Entry**: Quickly log your time with our intuitive timesheet interface
- **Approvals**: Streamlined approval workflow for managers
- **Reports**: Comprehensive reporting and analytics
- **Team Management**: Manage team members and capacity
- **Integrations**: Connect with your ERP and HR systems

## Quick Start
1. Navigate to "My Timesheet" to start logging time
2. Use the Quick Entry feature for fast time logging
3. Submit your timesheet for approval at the end of the week
4. Managers can review and approve timesheets in the Approvals section

## Next Steps
- Explore the Dashboard for an overview of your activities
- Check out the Reports section for detailed analytics
- Configure your preferences in Settings
    `,
    tags: ['basics', 'introduction', 'overview'],
    lastUpdated: '2024-01-15',
  },
  {
    id: 'timesheet-entry',
    title: 'How to Enter Time',
    category: 'User Guides',
    description: 'Step-by-step guide on entering and managing your timesheet entries.',
    content: `
# How to Enter Time

Learn how to efficiently log your time in TimeTrack Pro.

## Accessing Your Timesheet
1. Click on "My Timesheet" in the sidebar
2. Select the week you want to work on
3. The timesheet grid will display all days of the week

## Adding Time Entries
### Method 1: Direct Grid Entry
- Click on any cell in the timesheet grid
- Enter the number of hours worked
- The system automatically saves your entry

### Method 2: Quick Entry
- Click the "Quick Entry" button
- Describe your work in natural language (e.g., "8 hours on Project Alpha yesterday")
- The system will parse and create the entry automatically

## Editing Entries
- Click on any existing entry to edit
- Modify the hours, project, or description
- Changes are saved automatically

## Best Practices
- Log time daily for accuracy
- Use descriptive project names
- Review your timesheet before submitting
- Submit by the end of each week

## Tips
- Use keyboard shortcuts for faster entry
- Copy previous week's entries to save time
- Set up favorite projects for quick access
    `,
    tags: ['timesheet', 'time entry', 'how-to'],
    lastUpdated: '2024-01-14',
  },
  {
    id: 'approval-workflow',
    title: 'Approval Workflow Guide',
    category: 'User Guides',
    description: 'Understanding the timesheet approval process for managers and employees.',
    content: `
# Approval Workflow Guide

Learn how the approval workflow works in TimeTrack Pro.

## For Employees

### Submitting Your Timesheet
1. Complete all time entries for the week
2. Review your entries for accuracy
3. Click "Submit for Approval"
4. You'll receive a confirmation notification

### Tracking Approval Status
- View status in the Dashboard
- Check the "This Week" card for current status
- Receive notifications when approved or rejected

### Handling Rejections
- Review the rejection reason
- Make necessary corrections
- Resubmit for approval

## For Managers

### Reviewing Timesheets
1. Navigate to the "Approvals" section
2. View all pending timesheets
3. Review each entry for accuracy
4. Approve or reject with comments

### Bulk Actions
- Select multiple timesheets
- Approve or reject in bulk
- Add comments for rejected items

### Approval Rules
- Configure automatic approval rules in Settings
- Set thresholds for automatic approval
- Define escalation workflows

## Best Practices
- Review timesheets promptly
- Provide clear feedback on rejections
- Use bulk actions for efficiency
- Set up approval rules to automate routine approvals
    `,
    tags: ['approvals', 'workflow', 'managers'],
    lastUpdated: '2024-01-13',
  },
  {
    id: 'reports-overview',
    title: 'Reports and Analytics',
    category: 'User Guides',
    description: 'Comprehensive guide to generating and understanding reports.',
    content: `
# Reports and Analytics

Discover how to generate and interpret reports in TimeTrack Pro.

## Available Reports

### Timesheet Reports
- View detailed timesheet data
- Filter by employee, project, or date range
- Export to Excel or PDF

### Utilization Reports
- Track team utilization rates
- Identify capacity issues
- Optimize resource allocation

### Billable Hours Reports
- Analyze billable vs non-billable time
- Track project profitability
- Generate client invoices

### Project Reports
- Project-level time tracking
- Budget vs actual analysis
- Project health metrics

## Generating Reports
1. Navigate to the Reports section
2. Select the report type
3. Configure filters and parameters
4. Click "Generate Report"
5. Export or share as needed

## Report Filters
- Date Range: Select specific periods
- Employees: Filter by team members
- Projects: Focus on specific projects
- Status: Filter by approval status

## Exporting Reports
- **Excel**: Full data export with formatting
- **PDF**: Print-ready formatted reports
- **CSV**: Raw data for further analysis

## Scheduling Reports
- Set up automated report generation
- Schedule email delivery
- Configure report recipients
    `,
    tags: ['reports', 'analytics', 'data'],
    lastUpdated: '2024-01-12',
  },
  {
    id: 'quick-entry-guide',
    title: 'Quick Entry Feature',
    category: 'User Guides',
    description: 'Master the Quick Entry feature for fast time logging.',
    content: `
# Quick Entry Feature

Learn how to use the Quick Entry feature for rapid time logging.

## What is Quick Entry?
Quick Entry allows you to log time using natural language. Simply describe your work, and the system will create the appropriate timesheet entry.

## How to Use Quick Entry
1. Click the "Quick Entry" button
2. Type your time entry in natural language
3. The system parses your input
4. Review and confirm the entry

## Example Entries
- "8 hours on Project Alpha today"
- "4 hours meeting yesterday"
- "2.5 hours coding on Monday"
- "Full day on client work"

## Supported Formats
- **Time**: hours, h, hrs (e.g., "8 hours", "4h", "2.5 hrs")
- **Dates**: today, yesterday, Monday, last Friday
- **Projects**: Any project name in your list
- **Activities**: meeting, coding, testing, documentation

## Tips for Best Results
- Be specific about the project name
- Use clear time indicators
- Specify the date if not today
- Review the parsed entry before saving

## Advanced Features
- Bulk entry: Enter multiple items at once
- Templates: Save common entries as templates
- Voice input: Use voice-to-text for hands-free entry
    `,
    tags: ['quick entry', 'time entry', 'productivity'],
    lastUpdated: '2024-01-11',
  },
  {
    id: 'api-reference',
    title: 'API Reference',
    category: 'API Reference',
    description: 'Complete API documentation for developers and integrations.',
    content: `
# API Reference

Technical documentation for integrating with TimeTrack Pro.

## Authentication
All API requests require authentication using an API key.

\`\`\`
Authorization: Bearer YOUR_API_KEY
\`\`\`

## Endpoints

### Timesheets
**GET /api/timesheets**
- Retrieve timesheet data
- Query parameters: startDate, endDate, employeeId

**POST /api/timesheets**
- Create a new timesheet entry
- Body: { date, projectId, hours, description }

**PUT /api/timesheets/:id**
- Update an existing entry
- Body: { hours, description }

**DELETE /api/timesheets/:id**
- Delete a timesheet entry

### Approvals
**GET /api/approvals/pending**
- Get pending approvals
- Query parameters: managerId

**POST /api/approvals/:id/approve**
- Approve a timesheet
- Body: { comments }

**POST /api/approvals/:id/reject**
- Reject a timesheet
- Body: { reason, comments }

### Reports
**GET /api/reports/utilization**
- Generate utilization report
- Query parameters: startDate, endDate, teamId

**GET /api/reports/timesheet**
- Generate timesheet report
- Query parameters: startDate, endDate, employeeId

## Response Formats
All responses are in JSON format.

### Success Response
\`\`\`json
{
  "success": true,
  "data": { ... }
}
\`\`\`

### Error Response
\`\`\`json
{
  "success": false,
  "error": {
    "code": "ERROR_CODE",
    "message": "Error description"
  }
}
\`\`\`

## Rate Limits
- 1000 requests per hour per API key
- Burst limit: 100 requests per minute

## Webhooks
Configure webhooks to receive real-time notifications:
- Timesheet submitted
- Timesheet approved
- Timesheet rejected
    `,
    tags: ['api', 'integration', 'developers'],
    lastUpdated: '2024-01-10',
  },
  {
    id: 'faq',
    title: 'Frequently Asked Questions',
    category: 'FAQs',
    description: 'Common questions and answers about TimeTrack Pro.',
    content: `
# Frequently Asked Questions

Find answers to common questions about TimeTrack Pro.

## General Questions

### What is TimeTrack Pro?
TimeTrack Pro is a comprehensive time tracking and management solution designed for professional services organizations.

### Who can use TimeTrack Pro?
TimeTrack Pro is designed for employees, managers, and administrators in professional services firms.

### Is my data secure?
Yes, we use industry-standard encryption and security practices to protect your data.

## Time Entry

### How do I log time?
You can log time using the timesheet grid or the Quick Entry feature. See the "How to Enter Time" guide for details.

### Can I edit submitted timesheets?
Once submitted, timesheets can only be edited if rejected by a manager. Contact your manager if you need to make changes.

### What if I forget to log time?
You can log time retroactively for previous weeks. However, it's best to log time daily for accuracy.

## Approvals

### How long does approval take?
Approval times vary by organization. Most timesheets are approved within 1-2 business days.

### What happens if my timesheet is rejected?
You'll receive a notification with the reason for rejection. Make the necessary corrections and resubmit.

### Can I see who approved my timesheet?
Yes, approval history is available in the timesheet details view.

## Reports

### How do I export reports?
All reports can be exported to Excel, PDF, or CSV format using the export button.

### Can I schedule automated reports?
Yes, you can set up automated report generation and email delivery in the Reports settings.

### How far back can I generate reports?
You can generate reports for any historical period, subject to your organization's data retention policy.

## Technical Issues

### The application is slow. What should I do?
Try clearing your browser cache and refreshing the page. If the issue persists, contact support.

### I can't log in. What should I do?
Verify your credentials and check your internet connection. If you still can't log in, use the "Forgot Password" link or contact support.

### My data isn't syncing. What should I do?
Check the Sync Status page under Integrations. If there are sync errors, contact your administrator.
    `,
    tags: ['faq', 'help', 'troubleshooting'],
    lastUpdated: '2024-01-09',
  },
  {
    id: 'integrations',
    title: 'Integration Setup Guide',
    category: 'User Guides',
    description: 'How to set up and configure integrations with external systems.',
    content: `
# Integration Setup Guide

Learn how to connect TimeTrack Pro with your existing systems.

## Supported Integrations

### ERP Systems
- Access Financials
- Access Dimensions
- SAP
- Oracle

### HR Systems
- People HR
- PayCircle
- BambooHR
- Workday

### Project Management
- Jira
- Asana
- Monday.com
- Microsoft Project

## Setting Up Integrations

### Step 1: Access Integration Settings
1. Navigate to Settings > Integrations
2. Select the system you want to integrate
3. Click "Configure"

### Step 2: Authentication
1. Enter your API credentials
2. Test the connection
3. Save the configuration

### Step 3: Configure Sync Settings
1. Choose what data to sync
2. Set sync frequency
3. Configure field mappings

### Step 4: Test and Verify
1. Run a test sync
2. Verify data accuracy
3. Monitor sync status

## Sync Frequency
- Real-time: Immediate sync (premium feature)
- Hourly: Sync every hour
- Daily: Sync once per day
- Manual: Sync on demand

## Troubleshooting
- Check API credentials
- Verify network connectivity
- Review sync logs
- Contact support if issues persist

## Best Practices
- Start with a test environment
- Monitor sync status regularly
- Keep API credentials secure
- Document custom field mappings
    `,
    tags: ['integrations', 'setup', 'configuration'],
    lastUpdated: '2024-01-08',
  },
];

export const DocumentationPage: React.FC<DocumentationPageProps> = ({ onNavigate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<DocumentationArticle | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [feedback, setFeedback] = useState<{ [key: string]: 'helpful' | 'not-helpful' | null }>({});

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ['all', ...new Set(documentationData.map((doc) => doc.category))];
    return cats;
  }, []);

  // Filter articles based on search and category
  const filteredArticles = useMemo(() => {
    return documentationData.filter((article) => {
      const matchesSearch =
        searchQuery === '' ||
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  // Group articles by category
  const articlesByCategory = useMemo(() => {
    const grouped: { [key: string]: DocumentationArticle[] } = {};
    filteredArticles.forEach((article) => {
      if (!grouped[article.category]) {
        grouped[article.category] = [];
      }
      grouped[article.category].push(article);
    });
    return grouped;
  }, [filteredArticles]);

  const handleFeedback = (articleId: string, isHelpful: boolean) => {
    setFeedback((prev) => ({
      ...prev,
      [articleId]: isHelpful ? 'helpful' : 'not-helpful',
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/' },
    { label: 'Help & Support' },
    { label: 'Documentation' },
  ];

  const renderBreadcrumbs = () => (
    <nav aria-label="Breadcrumb" className="flex items-center space-x-1 text-sm text-muted-foreground mb-6">
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;
        return (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRight className="h-4 w-4" />}
            {item.path ? (
              <button
                onClick={() => onNavigate?.(item.path!)}
                className="flex items-center gap-1.5 hover:text-foreground transition-colors"
              >
                {index === 0 && <Home className="h-4 w-4" />}
                <span>{item.label}</span>
              </button>
            ) : (
              <span className={cn(isLast && 'text-foreground font-medium')}>
                {item.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );

  const renderArticleList = () => (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
          <TabsList className="w-full justify-start flex-wrap h-auto">
            {categories.map((category) => (
              <TabsTrigger key={category} value={category} className="capitalize">
                {category === 'all' ? 'All Topics' : category}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Results Count */}
      {searchQuery && (
        <Alert>
          <Search className="h-4 w-4" />
          <AlertDescription>
            Found {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} matching "{searchQuery}"
          </AlertDescription>
        </Alert>
      )}

      {/* Articles by Category */}
      {Object.keys(articlesByCategory).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No articles found matching your search.</p>
            <Button variant="outline" className="mt-4" onClick={() => setSearchQuery('')}>
              Clear Search
            </Button>
          </CardContent>
        </Card>
      ) : (
        Object.entries(articlesByCategory).map(([category, articles]) => (
          <div key={category} className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              {category === 'Getting Started' && <Rocket className="h-5 w-5 text-primary" />}
              {category === 'User Guides' && <FileText className="h-5 w-5 text-primary" />}
              {category === 'FAQs' && <HelpCircle className="h-5 w-5 text-primary" />}
              {category === 'API Reference' && <Code className="h-5 w-5 text-primary" />}
              {category}
            </h2>
            <div className="grid gap-3">
              {articles.map((article) => (
                <Card
                  key={article.id}
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={() => setSelectedArticle(article)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <CardTitle className="text-base">{article.title}</CardTitle>
                        <CardDescription className="mt-1.5">{article.description}</CardDescription>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {article.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      <span className="text-xs text-muted-foreground ml-auto">
                        Updated {new Date(article.lastUpdated).toLocaleDateString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderArticleView = () => {
    if (!selectedArticle) return null;

    return (
      <div className="space-y-6">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => setSelectedArticle(null)} className="mb-2">
          <ChevronRight className="h-4 w-4 mr-2 rotate-180" />
          Back to Documentation
        </Button>

        {/* Article Header */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline">{selectedArticle.category}</Badge>
            <span className="text-sm text-muted-foreground">
              Last updated: {new Date(selectedArticle.lastUpdated).toLocaleDateString()}
            </span>
          </div>
          <h1 className="text-3xl font-bold mb-2">{selectedArticle.title}</h1>
          <p className="text-muted-foreground">{selectedArticle.description}</p>
        </div>

        <Separator />

        {/* Article Content */}
        <Card>
          <CardContent className="pt-6">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {selectedArticle.content.split('\n').map((line, index) => {
                // Simple markdown-like rendering
                if (line.startsWith('# ')) {
                  return (
                    <h1 key={index} className="text-2xl font-bold mt-6 mb-4">
                      {line.substring(2)}
                    </h1>
                  );
                }
                if (line.startsWith('## ')) {
                  return (
                    <h2 key={index} className="text-xl font-semibold mt-5 mb-3">
                      {line.substring(3)}
                    </h2>
                  );
                }
                if (line.startsWith('### ')) {
                  return (
                    <h3 key={index} className="text-lg font-semibold mt-4 mb-2">
                      {line.substring(4)}
                    </h3>
                  );
                }
                if (line.startsWith('- ')) {
                  return (
                    <li key={index} className="ml-4">
                      {line.substring(2)}
                    </li>
                  );
                }
                if (line.startsWith('**') && line.endsWith('**')) {
                  return (
                    <p key={index} className="font-semibold my-2">
                      {line.substring(2, line.length - 2)}
                    </p>
                  );
                }
                if (line.startsWith('```')) {
                  return null; // Skip code fence markers
                }
                if (line.trim() === '') {
                  return <div key={index} className="h-2" />;
                }
                return (
                  <p key={index} className="my-2">
                    {line}
                  </p>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Tags */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">Tags:</span>
          {selectedArticle.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>

        <Separator />

        {/* Feedback Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Was this article helpful?</CardTitle>
          </CardHeader>
          <CardContent>
            {feedback[selectedArticle.id] ? (
              <Alert>
                <AlertDescription>
                  Thank you for your feedback! We appreciate your input.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleFeedback(selectedArticle.id, true)}
                  className="flex-1"
                >
                  <ThumbsUp className="h-4 w-4 mr-2" />
                  Yes, this was helpful
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleFeedback(selectedArticle.id, false)}
                  className="flex-1"
                >
                  <ThumbsDown className="h-4 w-4 mr-2" />
                  No, needs improvement
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Print Button */}
        <div className="flex justify-end">
          <Button variant="outline" onClick={handlePrint}>
            Print Article
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      {renderBreadcrumbs()}

      {/* Page Header */}
      {!selectedArticle && (
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
              <h1 className="text-3xl font-bold">Documentation</h1>
            </div>
            <p className="text-muted-foreground">
              Comprehensive guides and documentation to help you get the most out of TimeTrack Pro
            </p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Only show when viewing article list */}
        {!selectedArticle && (
          <div className="lg:col-span-1">
            <Card className="sticky top-20">
              <CardHeader>
                <CardTitle className="text-base">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    setSelectedCategory('Getting Started');
                    setSearchQuery('');
                  }}
                >
                  <Rocket className="h-4 w-4 mr-2" />
                  Getting Started
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    setSelectedCategory('User Guides');
                    setSearchQuery('');
                  }}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  User Guides
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    setSelectedCategory('FAQs');
                    setSearchQuery('');
                  }}
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  FAQs
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    setSelectedCategory('API Reference');
                    setSearchQuery('');
                  }}
                >
                  <Code className="h-4 w-4 mr-2" />
                  API Reference
                </Button>
                <Separator className="my-4" />
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => onNavigate?.('/help/support')}
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <div className={cn('lg:col-span-3', selectedArticle && 'lg:col-span-4')}>
          {selectedArticle ? renderArticleView() : renderArticleList()}
        </div>
      </div>
    </div>
  );
};
